import { useMutation } from '@tanstack/react-query';
import {
  AgxColours,
  AgxFileUpload,
  AgxToast,
  EntityImage,
  Images,
} from '@urbanx/agx-ui-components';
import {
  Team,
  TeamMember,
  UploadedDocument,
  useGetAgencyMembers,
  useGetTeamsForAgency,
  useInvalidateUseGetAgencyMembers,
  useInvalidateUseGetTeamsForAgency,
  useSetTeamsForAgency,
} from '@urbanx/agx.agencies.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  AgxButton,
  AgxColumn,
  AgxDivider,
  AgxRow,
  AgxSaveChangesButton,
  AgxSelectChip,
  AgxTextInput,
  AgxTypography,
  Icon_Arrow,
  SAVE_BUTTON_STATES,
  SelectChipOption,
} from '@urbanx/agx.ui-components2';
import LoadingState from 'components/loadingState/LoadingState';
import { cloneDeep, isEqual } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UploadPublicDocument } from 'services';
import SortableTeamMembers from './SortableTeamMembers';
import { AgxToastState } from 'types/commonTypes';
import { isDirtyEqual } from 'utils/compares';

import './AddOrEditTeamPage.scss';
import Panel from 'components/panel/Panel';
import CommunicationPreferences from './CommunicationPreferences';
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation';

interface AddOrEditTeamPageProps {
  isEdit?: boolean;
}

const defaultTeamForm: Team = {
  members: [],
  name: '',
  id: undefined,
  photoUri: undefined,
};

const cloneTeamForm = (team?: Team): Team => cloneDeep(team ?? defaultTeamForm);

const AddOrEditTeamPage = ({ isEdit = false }: AddOrEditTeamPageProps) => {
  const navigate = useNavigate();

  const { teamId } = useParams();
  const { agencyId } = useParams();

  const { data: teamsForAgency, isPending: isTeamsForAgencyPending } = useGetTeamsForAgency(
    agencyId ?? '',
    !!agencyId
  );

  const { data: agencyMembers, isPending: isAgencyMembersPending } = useGetAgencyMembers(
    agencyId ?? '',
    !!agencyId
  );

  const isAnythingLoading = useMemo(
    () => isTeamsForAgencyPending || isAgencyMembersPending,
    [isTeamsForAgencyPending, isAgencyMembersPending]
  );

  const selectedTeam = useMemo(() => {
    if (!isEdit || !teamId || !teamsForAgency) return undefined;

    return teamsForAgency.find((team) => team.id === teamId);
  }, [teamsForAgency, teamId, isEdit]);

  const onBackClicked = useCallback(() => {
    navigate('../manage-teams');
  }, [navigate]);

  const [teamForm, updateTeamForm] = useState<Team>(() => cloneTeamForm(selectedTeam));
  const [initialTeamForm, setInitialTeamForm] = useState<Team>(() => cloneTeamForm(selectedTeam));
  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  );

  useEffect(() => {
    if (!isEdit) {
      updateTeamForm(cloneTeamForm());
      setInitialTeamForm(cloneTeamForm());
      setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
    }
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !selectedTeam) return;

    updateTeamForm(cloneTeamForm(selectedTeam));
    setInitialTeamForm(cloneTeamForm(selectedTeam));
    setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
  }, [selectedTeam?.id, isEdit, teamId]);

  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const onNameUpdate = useCallback(
    (name: string) => {
      if (name === teamForm?.name) return;

      if (name.trim().length === 0) {
        setNameError('Team name is required.');
        return;
      }

      if (name.length > 0 && teamsForAgency?.some((t) => t.name === name)) {
        setNameError('Team name already exists.');
        return;
      }

      setNameError(undefined);

      updateTeamForm((prev) => ({
        ...prev,
        name,
      }));
    },
    [teamForm?.name, updateTeamForm, teamsForAgency]
  );

  const { mutate: uploadPublicDocument } = useMutation({
    mutationFn: UploadPublicDocument,
    onSuccess: (r) => {
      updateTeamForm((prev) => ({
        ...prev,
        photoUri: r?.publicPath || '',
      }));
    },
  });

  const { getAuthToken } = useContext(AuthContext);

  const uploadLogo = useCallback(
    (file: File) => {
      updateTeamForm((prev) => ({
        ...prev,
        photoUri: URL.createObjectURL(file),
      }));
      return new Promise((resolve, reject) => {
        uploadPublicDocument(
          {
            selectedFile: file,
            AgencyId: agencyId,
            getAuthToken,
          },
          {
            onSuccess: resolve,
            onError: reject,
          }
        );
      });
    },
    [updateTeamForm, uploadPublicDocument, agencyId, getAuthToken]
  );

  const orderedMembersList = useMemo(() => {
    return teamForm.members?.map((m) => m.agentId!) ?? [];
  }, [teamForm.members]);

  const onTeamMemberSelectionChanged = useCallback(
    (options: SelectChipOption[]) => {
      const selectedOptions = options.filter((o) => o.selected).map((o) => o.value);

      const selectedTeamMembers =
        agencyMembers?.filter((member) => selectedOptions.includes(member.id!)) ?? [];

      if (isEqual(selectedTeamMembers, teamForm.members)) return;

      const newMembers: TeamMember[] = selectedTeamMembers
        .filter((member) => !teamForm.members?.some((m) => m.agentId === member.id))
        .map((member) => ({
          agentId: member.id!,
          communicationPreferences: undefined,
        }));

      const existingMembers =
        teamForm.members?.filter((member) => selectedOptions.includes(member.agentId!)) ?? [];

      updateTeamForm((prev) => ({
        ...prev,
        members: [...existingMembers, ...newMembers],
      }));
    },
    [agencyMembers, teamForm.members]
  );

  const potentialMembersList: SelectChipOption[] = useMemo(() => {
    return (
      agencyMembers?.map((member) => ({
        value: member.id || '',
        label: `${member.firstName} ${member.lastName}`,
        selected: teamForm.members?.some((m) => m.agentId === member.id),
      })) || []
    );
  }, [agencyMembers, teamForm.members]);

  const onTeamMemberOrderUpdated = useCallback(
    (newOrder: string[]) => {
      const newOrderedMembers = newOrder
        .map((id) => teamForm.members?.find((member) => member.agentId === id))
        .filter(Boolean) as TeamMember[];

      updateTeamForm((prev) => ({
        ...prev,
        members: newOrderedMembers,
      }));
    },
    [teamForm.members]
  );

  const invalidateTeamsForAgency = useInvalidateUseGetTeamsForAgency();
  const invalidateAgencyMembers = useInvalidateUseGetAgencyMembers();

  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  const isFormDirty = useMemo(() => {
    return !isDirtyEqual(teamForm, initialTeamForm);
  }, [teamForm, initialTeamForm]);

  const { setTeamsForAgency, setTeamsForAgencyPending } = useSetTeamsForAgency({
    onSuccess: async () => {
      await invalidateTeamsForAgency(agencyId ?? '');
      await invalidateAgencyMembers(agencyId ?? '');

      setInitialTeamForm(cloneTeamForm(teamForm));
      setSaveButtonState(SAVE_BUTTON_STATES.SAVED);
      updateToastState({
        color: 'success',
        message: isEdit ? 'Changes saved successfully' : 'Changes Saved',
        open: true,
      });
    },
    onError: () => {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      updateToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      });
    },
  });

  const saveChangesState = useMemo(() => {
    if (setTeamsForAgencyPending) return SAVE_BUTTON_STATES.SAVING;
    if (saveButtonState === SAVE_BUTTON_STATES.ERROR) return SAVE_BUTTON_STATES.ERROR;
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED;
    return saveButtonState;
  }, [isFormDirty, saveButtonState, setTeamsForAgencyPending]);

  useEffect(() => {
    if (isFormDirty) {
      setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
    }
  }, [isFormDirty]);

  const onSaveChangesClicked = useCallback(() => {
    if (setTeamsForAgencyPending || !isFormDirty) return;

    if (!teamForm.name?.trim()) {
      setNameError('Team name is required.');
      return;
    }

    if (nameError) return;

    let updatedTeams: Team[] = [];

    if (isEdit) {
      updatedTeams =
        teamsForAgency?.map((team) => (team.id === teamForm.id ? teamForm : team)) ?? [];
    } else {
      updatedTeams = [...(teamsForAgency ?? []), teamForm];
    }

    setTeamsForAgency({
      agencyId: agencyId ?? '',
      agencyTeams: updatedTeams,
    });
  }, [
    setTeamsForAgencyPending,
    isFormDirty,
    teamForm,
    isEdit,
    teamsForAgency,
    setTeamsForAgency,
    agencyId,
    nameError,
  ]);

  const onConfirmDeleteTeamClicked = useCallback(async () => {
    if (!isEdit) {
      setShowDeleteConfirmation(false);
      return;
    }

    const updatedTeams = teamsForAgency?.filter((team) => team.id !== teamForm.id) ?? [];

    await setTeamsForAgency({
      agencyId: agencyId ?? '',
      agencyTeams: updatedTeams,
    });

    setShowDeleteConfirmation(false);
  }, [isEdit, teamsForAgency, teamForm, setTeamsForAgency, agencyId]);

  if (isAnythingLoading || (isEdit && !selectedTeam)) {
    return <LoadingState />;
  }

  return (
    <AgxColumn fill gap="spacing-3xl">
      <AgxToast selector="#agxToast" toastState={toastState} />

      <AgxRow fill justifyContent="space-between" alignItems="alignCenter">
        <AgxButton
          id="return-button"
          variant="naked"
          text={`${isEdit ? 'Edit' : 'Add'} Team`}
          leftIcon={<Icon_Arrow direction="left" height={22} width={22} />}
          onClick={onBackClicked}
        />
        <AgxRow gap="spacing-lg" alignItems="alignCenter">
          {isEdit && (
            <AgxButton
              id={'deleteTeam'}
              text={'Delete Team'}
              variant={'naked'}
              size="medium"
              danger
              onClick={() => setShowDeleteConfirmation(true)}
            />
          )}
          <AgxSaveChangesButton
            id="save-team-button"
            text="Save Changes"
            variant={'primary'}
            size="medium"
            state={saveChangesState}
            onClick={onSaveChangesClicked}
            disabled={!isFormDirty || setTeamsForAgencyPending || !!nameError}
          />
        </AgxRow>
      </AgxRow>
      <AgxDivider variant={'horizontal'} />

      <Panel>
        <AgxColumn fill gap="spacing-3xl">
          <AgxTextInput
            id="txtTeamName"
            type="text"
            label=""
            size="medium"
            hideOptionalText
            placeholder="Team 1"
            leftIcon={<Images.PeopleBlack />}
            stretch
            defaultValue={teamForm.name}
            errorText={nameError}
            onInputValueChange={({ value }: { value: string }) => onNameUpdate(value)}
          />
          <AgxDivider variant="horizontal" />
          <AgxRow gap="spacing-lg" fill alignItems="alignCenter">
            <EntityImage
              name={teamForm.name}
              addRingToImage
              backgroundColour={`var(--${AgxColours.NEUTRAL_GREY_500})`}
              photoUri={teamForm.photoUri}
              size={72}
              usePlaceholderImage
            />
            <AgxRow fill gap="spacing-lg" alignItems="alignCenter">
              <AgxFileUpload
                id="fileUpload"
                title=""
                buttonText={teamForm.photoUri ? 'Change' : 'Upload'}
                isImageFileType
                uploadFile={(file) => uploadLogo(file)}
                extensions={['jpg', 'jpeg', 'png', 'svg', 'bmp']}
              />
              {teamForm.photoUri && (
                <AgxButton
                  id="btn-remove"
                  text="Remove"
                  size="medium"
                  variant="hollow"
                  danger
                  onClick={() => {
                    updateTeamForm((prev) => ({
                      ...prev,
                      photoUri: undefined,
                    }));
                  }}
                />
              )}
            </AgxRow>
          </AgxRow>

          <AgxDivider variant="horizontal" />

          <AgxSelectChip
            id={`team-members-select-chip`}
            label="Team Members"
            defaultSelection={orderedMembersList}
            onSelect={onTeamMemberSelectionChanged}
            options={potentialMembersList}
            size={'large'}
            type="multi-select"
            variant={'chip'}
            maxLabels={100}
          />

          <div id="sortable-team-members-container">
            <SortableTeamMembers
              orderedMembersList={orderedMembersList}
              agencyMembers={agencyMembers ?? []}
              onUseAgentImage={(name: string, photo?: UploadedDocument) => {
                updateTeamForm((prev) => ({
                  ...prev,
                  name: name,
                  photoUri: photo?.publicPath ?? '',
                }));
              }}
              orderUpdated={onTeamMemberOrderUpdated}
            />
          </div>

          <AgxTypography id="communication-preferences-title" type="label" size="medium">
            Communication Preferences
          </AgxTypography>

          <CommunicationPreferences
            members={teamForm.members ?? []}
            onPreferencesChange={(members: TeamMember[]): void => {
              updateTeamForm((prev) => ({
                ...prev,
                members: members,
              }));
            }}
            agents={agencyMembers ?? []}
          />
        </AgxColumn>
      </Panel>

      <DeleteConfirmation
        name={selectedTeam?.name || ''}
        title="Delete team?"
        lineOne="Are you sure you want to delete"
        lineTwo="This cannot be undone."
        primaryActionTitle="Permanently delete team"
        secondaryActionTitle="No, cancel"
        showPopUp={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={onConfirmDeleteTeamClicked}
      />
    </AgxColumn>
  );
};

export default AddOrEditTeamPage;
